package com.eladfinish.bible.contest.screens.lexicographicalSort.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.lexicographical_sort_choose_book_and_chapter
import com.eladfinish.bible.contest.lexicographical_sort_generate_table
import com.eladfinish.bible.contest.lexicographical_sort_premium_required
import com.eladfinish.bible.contest.lexicographical_sort_verses_in_table
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.OutputChapters
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortAction
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortEffect
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortState
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortStore
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.common.VerticalSpacer
import com.eladfinish.bible.contest.ui.components.buttons.PrimaryButtonText
import com.eladfinish.bible.contest.ui.components.dialogs.BasicDialogWithTitle
import com.eladfinish.bible.contest.ui.components.verticalScrollWithScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.collections.immutable.toImmutableList
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun LexicographicalSortScreen(
    store: LexicographicalSortStore = koinViewModel(),
    showToastStringResource: (message: StringResource, snackbarDuration: SnackbarDuration) -> Unit,
    navigateToPdfViewer: (bookIndex: Int, chapterIndex: Int, outputChaptersOrdinal: Int) -> Unit,
) {
    val state by store.observeState().collectAsState()

    LaunchedEffect(Unit) {
        store.observeSideEffect().collect { effect ->
            when (effect) {
                is LexicographicalSortEffect.ShowToastStringResource -> {
                    showToastStringResource(effect.resource, effect.snackbarDuration)
                }

                is LexicographicalSortEffect.NavigateToPdfViewer -> {
                    navigateToPdfViewer(effect.bookIndex, effect.chapterIndex, effect.outputChaptersOrdinal)
                }
            }
        }
    }

    LexicographicalSortContent(
        state = state,
        onAction = store::dispatch,
    )

    when (val dialogState = state.dialogState) {
        LexicographicalSortState.DialogState.None -> Unit

        is LexicographicalSortState.DialogState.PremiumRequired -> {
            BasicDialogWithTitle(
                title = stringResource(SharedRes.strings.lexicographical_sort_premium_required),
                onDismiss = { store.dispatch(LexicographicalSortAction.DismissDialog) },
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text(dialogState.message)
                }
            }
        }
    }
}

@Composable
private fun LexicographicalSortContent(
    state: LexicographicalSortState,
    onAction: (LexicographicalSortAction) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScrollWithScrollbar()
            .navigationBarInsetPadding()
            .padding(16.dp),
    ) {
        Text(
            text = stringResource(SharedRes.strings.lexicographical_sort_choose_book_and_chapter),
            style = MaterialTheme.typography.titleMedium,
        )

        VerticalSpacer()

        LexicographicalSortBookChapterPicker(
            bookNames = state.bookNames,
            selectedBookIndex = state.selectedBookIndex,
            selectedChapterIndex = state.selectedChapterIndex,
            numberOfChaptersInSelectedBook = state.numberOfChaptersInSelectedBook,
            onBookSelected = { onAction(LexicographicalSortAction.BookSelected(it)) },
            onChapterSelected = { onAction(LexicographicalSortAction.ChapterSelected(it)) },
        )

        Spacer(modifier = Modifier.height(16.dp))

        HorizontalDivider()

        Spacer(modifier = Modifier.height(16.dp))

        LexicographicalSortOutputPicker(
            selected = state.selectedOutputChapters,
            onSelect = { onAction(LexicographicalSortAction.OutputChaptersSelected(it)) },
        )

        Spacer(modifier = Modifier.height(16.dp))

        Text(
            text = stringResource(
                SharedRes.strings.lexicographical_sort_verses_in_table,
                state.versesInTable,
            ),
            style = MaterialTheme.typography.bodyLarge,
        )

        Spacer(modifier = Modifier.height(8.dp))

        PrimaryButtonText(
            text = stringResource(SharedRes.strings.lexicographical_sort_generate_table),
            enabled = state.isCreateTableEnabled,
            onClick = { onAction(LexicographicalSortAction.GenerateClicked) },
        )
    }
}

private fun previewState(): LexicographicalSortState {
    val bookNames = BooksRepository.bookNamesHebrew.toImmutableList()
    val selectedBookIndex = BooksRepository.bookNamesHebrew.indexOf("משלי") + 1
    return LexicographicalSortState(
        bookNames = bookNames,
        selectedBookIndex = selectedBookIndex,
        selectedChapterIndex = 22,
        numberOfChaptersInSelectedBook = BooksRepository.numOfChaptersPerBook[selectedBookIndex - 1],
        selectedOutputChapters = OutputChapters.SINGLE,
        versesInTable = 11,
        dialogState = LexicographicalSortState.DialogState.None,
    )
}

@Preview
@Composable
private fun LexicographicalSortContentPreview() {
    PreviewTheme {
        LexicographicalSortContent(
            state = previewState(),
            onAction = {},
        )
    }
}
